import React, { useEffect, useState } from "react";
import { getSupabaseClient } from "../../../lib/supabase";
import { Guide } from "../../../types/guide";
import { cardStyle } from "../GuideDetailModal.styles";

type Review = {
  id: string;
  offer_id: string;
  guide_id: string;
  seeker_id: string;
  rating: number;
  title: string | null;
  comment: string | null;
  would_recommend: boolean | null;
  is_anonymous: boolean;
  visibility: 'internal' | 'public';
  created_at: string;
  updated_at: string;
  seeker_name?: string;
  source: 'platform' | 'off_platform';
  reviewer_name?: string;
};

export function ReviewsTab({ guide }: { guide: Guide }) {
  const [reviews, setReviews] = useState<Review[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [deletingId, setDeletingId] = useState<string | null>(null);
  const [hidingId, setHidingId] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;

    async function loadReviews() {
      try {
        const supabase = getSupabaseClient();

        // Fetch platform reviews for this guide
        const { data: platformReviews, error: platformError } = await supabase
          .from('guide_reviews')
          .select('*')
          .eq('guide_id', guide.id)
          .order('created_at', { ascending: false });

        if (platformError) throw platformError;

        // Fetch off-platform reviews for this guide
        const { data: offPlatformReviews, error: offPlatformError } = await supabase
          .from('off_platform_guide_reviews')
          .select('*')
          .eq('guide_id', guide.id)
          .eq('is_submitted', true)
          .order('created_at', { ascending: false });

        if (offPlatformError) {
          console.error('Error fetching off-platform reviews:', offPlatformError);
        }

        if (!cancelled) {
          // Fetch seeker names for non-anonymous platform reviews
          const seekerIds = (platformReviews || [])
            .filter(r => !r.is_anonymous)
            .map(r => r.seeker_id);

          let seekerNames = new Map<string, string>();

          if (seekerIds.length > 0) {
            const { data: profiles } = await supabase
              .from('profiles')
              .select('id, full_name')
              .in('id', seekerIds);

            if (profiles) {
              profiles.forEach(p => {
                if (p.id && p.full_name) {
                  seekerNames.set(p.id, p.full_name);
                }
              });
            }
          }

          // Normalize platform reviews
          const normalizedPlatformReviews: Review[] = (platformReviews || []).map(review => ({
            ...review,
            seeker_name: review.is_anonymous
              ? 'Anonymous'
              : seekerNames.get(review.seeker_id) || 'Unknown',
            source: 'platform' as const,
          }));

          // Normalize off-platform reviews
          const normalizedOffPlatformReviews: Review[] = (offPlatformReviews || []).map(review => ({
            id: review.id,
            offer_id: '',
            guide_id: review.guide_id,
            seeker_id: '',
            rating: review.rating,
            title: review.title,
            comment: review.body,
            would_recommend: null,
            is_anonymous: false,
            visibility: review.visibility,
            created_at: review.created_at,
            updated_at: review.updated_at,
            seeker_name: review.reviewer_name || 'Off-platform reviewer',
            source: 'off_platform' as const,
            reviewer_name: review.reviewer_name,
          }));

          // Combine and sort by date
          const allReviews = [...normalizedPlatformReviews, ...normalizedOffPlatformReviews]
            .sort((a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime());

          setReviews(allReviews);
        }
      } catch (err) {
        if (!cancelled) {
          console.error('Failed to load reviews:', err);
          setError('Failed to load reviews');
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    }

    loadReviews();

    return () => {
      cancelled = true;
    };
  }, [guide.id]);

  const handleDeleteReview = async (reviewId: string, source: 'platform' | 'off_platform') => {
    if (!confirm('Are you sure you want to permanently delete this review? This action cannot be undone.')) {
      return;
    }

    setDeletingId(reviewId);
    try {
      const supabase = getSupabaseClient();
      const tableName = source === 'platform' ? 'guide_reviews' : 'off_platform_guide_reviews';
      const { error: deleteError } = await supabase
        .from(tableName)
        .delete()
        .eq('id', reviewId);

      if (deleteError) throw deleteError;

      // Remove from local state
      setReviews(prev => prev.filter(r => r.id !== reviewId));
    } catch (err) {
      console.error('Failed to delete review:', err);
      alert('Failed to delete review. Please try again.');
    } finally {
      setDeletingId(null);
    }
  };

  const handleToggleVisibility = async (reviewId: string, currentVisibility: 'internal' | 'public', source: 'platform' | 'off_platform') => {
    const newVisibility = currentVisibility === 'public' ? 'internal' : 'public';
    const action = newVisibility === 'internal' ? 'hide' : 'make public';

    if (!confirm(`Are you sure you want to ${action} this review?`)) {
      return;
    }

    setHidingId(reviewId);
    try {
      const supabase = getSupabaseClient();
      const tableName = source === 'platform' ? 'guide_reviews' : 'off_platform_guide_reviews';
      const { error: updateError } = await supabase
        .from(tableName)
        .update({ visibility: newVisibility })
        .eq('id', reviewId);

      if (updateError) throw updateError;

      // Update local state
      setReviews(prev => prev.map(r =>
        r.id === reviewId ? { ...r, visibility: newVisibility } : r
      ));
    } catch (err) {
      console.error('Failed to update review visibility:', err);
      alert('Failed to update review visibility. Please try again.');
    } finally {
      setHidingId(null);
    }
  };

  if (loading) {
    return <div>Loading reviews...</div>;
  }

  if (error) {
    return <div style={{ color: '#DC2626' }}>{error}</div>;
  }

  if (reviews.length === 0) {
    return (
      <div style={cardStyle}>
        <p style={{ color: '#6B7280', textAlign: 'center', padding: '40px 20px' }}>
          No reviews yet
        </p>
      </div>
    );
  }

  // Calculate average rating
  const avgRating = reviews.length > 0
    ? (reviews.reduce((sum, r) => sum + r.rating, 0) / reviews.length).toFixed(1)
    : 'N/A';

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
      {/* Summary Card */}
      <div style={{
        ...cardStyle,
        background: '#F2EAC3',
        display: 'flex',
        gap: 32,
        alignItems: 'center',
        justifyContent: 'center',
      }}>
        <div style={{ textAlign: 'center' }}>
          <div style={{ fontSize: 36, fontWeight: 700, color: '#2C2731' }}>
            {avgRating}
          </div>
          <div style={{ fontSize: 13, color: '#403851', marginTop: 4 }}>
            Average Rating
          </div>
          <div style={{ fontSize: 12, color: '#6B7280', marginTop: 2 }}>
            {reviews.length} {reviews.length === 1 ? 'review' : 'reviews'}
          </div>
        </div>
      </div>

      {/* Individual Reviews */}
      {reviews.map((review) => (
        <div key={review.id} style={cardStyle}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start', marginBottom: 12 }}>
            <div style={{ flex: 1 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
                <div className="ph-mask" style={{ fontWeight: 600, fontSize: 15, color: '#2C2731' }}>
                  {review.seeker_name}
                </div>
                {review.is_anonymous && (
                  <span style={{
                    fontSize: 11,
                    padding: '2px 6px',
                    borderRadius: 4,
                    background: '#E5E7EB',
                    color: '#6B7280',
                  }}>
                    Anonymous
                  </span>
                )}
                {review.source === 'off_platform' && (
                  <span style={{
                    fontSize: 11,
                    padding: '2px 6px',
                    borderRadius: 4,
                    background: '#E0E7FF',
                    color: '#4338CA',
                  }}>
                    Off-platform
                  </span>
                )}
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                {[1, 2, 3, 4, 5].map((star) => (
                  <span
                    key={star}
                    style={{
                      fontSize: 16,
                      color: star <= review.rating ? '#FCD34D' : '#E5E7EB',
                    }}
                  >
                    ★
                  </span>
                ))}
                <span style={{ fontSize: 13, color: '#6B7280', marginLeft: 4 }}>
                  {review.rating}/5
                </span>
              </div>
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 8 }}>
              <div style={{ textAlign: 'right' }}>
                <div style={{ fontSize: 12, color: '#6B7280' }}>
                  {new Date(review.created_at).toLocaleDateString('en-US', {
                    month: 'short',
                    day: 'numeric',
                    year: 'numeric',
                  })}
                </div>
                <div style={{
                  fontSize: 11,
                  padding: '2px 6px',
                  borderRadius: 4,
                  background: review.visibility === 'public' ? '#DBEAFE' : '#FEF3C7',
                  color: review.visibility === 'public' ? '#1E40AF' : '#92400E',
                  marginTop: 4,
                }}>
                  {review.visibility === 'public' ? 'Public' : 'Internal'}
                </div>
              </div>
              <div style={{ display: 'flex', gap: 6 }}>
                <button
                  onClick={() => handleToggleVisibility(review.id, review.visibility, review.source)}
                  disabled={hidingId === review.id}
                  style={{
                    padding: '4px 10px',
                    fontSize: 12,
                    borderRadius: 6,
                    border: '1px solid #2C2731',
                    background: '#FEF6D6',
                    color: '#2C2731',
                    cursor: hidingId === review.id ? 'wait' : 'pointer',
                    fontWeight: 500,
                    opacity: hidingId === review.id ? 0.6 : 1,
                  }}
                  title={review.visibility === 'public' ? 'Hide review' : 'Make review public'}
                >
                  {hidingId === review.id ? '...' : (review.visibility === 'public' ? 'Hide' : 'Publish')}
                </button>
                <button
                  onClick={() => handleDeleteReview(review.id, review.source)}
                  disabled={deletingId === review.id}
                  style={{
                    padding: '4px 10px',
                    fontSize: 12,
                    borderRadius: 6,
                    border: '1px solid #DC2626',
                    background: '#FEE2E2',
                    color: '#DC2626',
                    cursor: deletingId === review.id ? 'wait' : 'pointer',
                    fontWeight: 500,
                    opacity: deletingId === review.id ? 0.6 : 1,
                  }}
                  title="Delete review permanently"
                >
                  {deletingId === review.id ? '...' : 'Delete'}
                </button>
              </div>
            </div>
          </div>

          {review.title && (
            <div className="ph-mask" style={{ fontWeight: 600, fontSize: 14, color: '#2C2731', marginBottom: 8 }}>
              {review.title}
            </div>
          )}

          {review.comment && (
            <div className="ph-mask" style={{ fontSize: 14, color: '#403851', lineHeight: 1.6, marginBottom: 12 }}>
              {review.comment}
            </div>
          )}

                  </div>
      ))}
    </div>
  );
}
